import { useParams, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import {
    IconTrash,
    IconUserCheck,
    IconListDetails,
    IconPlus,
    IconCalendarCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useUser, useDeleteUser } from '@/hooks/api-hooks';
import { SkeletonList } from '@/components/snippets';
import { localUser } from '@/services/session-service';
import { SETTINGS_USERS_PATH } from '@/lib/navigation';
import ShowMetadata from '@/components/show-metadata';
import { MENU_PERMISSIONS_LIST } from '@/components/left-menu';
import { CREATE_ACTION_PERMISSIONS_LIST } from '@/components/create-actions-menu';

export default function EditUserPage() {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const currentUser = localUser();

    const { data: userResponse, isLoading, isError } = useUser(id || '');
    const deleteUserMutation = useDeleteUser();
    const userData = userResponse?.data;

    const isSelf = currentUser && id && String(currentUser.id) === String(id);

    const handleDelete = async () => {
        if (!id) return;
        if (!confirm(t('confirm_delete_user'))) {
            return;
        }

        const { error } = await deleteUserMutation.mutateAsync(id);
        if (error) {
            notifications.show({
                title: t('error'),
                message: typeof error === 'string' ? error : error.message || t('unknown_error'),
                color: 'red',
            });
        } else {
            notifications.show({
                title: t('success'),
                message: t('user_deleted_successfully'),
                color: 'green',
            });
            navigate(SETTINGS_USERS_PATH);
        }
    };

    if (isLoading) {
        return (
            <div className="max-w-screen-xl space-y-6">
                <SkeletonList />
            </div>
        );
    }

    if (isError || !userData) {
        return (
            <div className="max-w-screen-xl p-8 bg-white rounded-2xl border border-gray-200 text-center space-y-4">
                <div className="text-lg font-bold text-gray-800">
                    {t('user_not_found')}
                </div>
            </div>
        );
    }

    const activeMenuCount = MENU_PERMISSIONS_LIST.filter(
        (item) => userData.permissions?.[item.key] !== false
    ).length;
    const activeCreateCount = CREATE_ACTION_PERMISSIONS_LIST.filter(
        (item) => userData.permissions?.[item.key] !== false
    ).length;

    const tabsConfig = [
        {
            to: 'details',
            labelKey: 'account_details',
            icon: IconUserCheck,
        },
        {
            to: 'menu',
            labelKey: 'menu_permissions',
            icon: IconListDetails,
            count: activeMenuCount,
            total: MENU_PERMISSIONS_LIST.length,
        },
        {
            to: 'create',
            labelKey: 'create_action_permissions',
            icon: IconPlus,
            count: activeCreateCount,
            total: CREATE_ACTION_PERMISSIONS_LIST.length,
        },
        {
            to: 'reservations',
            labelKey: 'reservations',
            icon: IconCalendarCheck,
        },
    ];

    return (
        <div className="max-w-screen-xl space-y-6">
            {/* 1. TOP SECTION: User Identity Card */}
            <div className="bg-white p-5 rounded-2xl border border-gray-200/90 shadow-xs flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="text-lg font-bold text-gray-900 leading-tight">
                            {userData.name || t('user')}
                        </div>
                        {isSelf && (
                            <div className="text-[10px] uppercase tracking-wide bg-emerald-600 text-white px-2 py-0.5 rounded font-bold">
                                {t('you')}
                            </div>
                        )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {userData.email} • #{userData.id}
                    </div>
                </div>
            </div>

            {/* 2. MIDDLE SECTION: Vertical Menu Navigation & Active Route Panel */}
            <div className="bg-white p-6 rounded-2xl border border-neutral-200/90 shadow-xs flex flex-col sm:flex-row items-start gap-0">
                {/* Custom Vertical Tabs List */}
                <div className="w-full sm:w-72 md:w-80 flex-shrink-0 space-y-1.5 pr-0 sm:pr-6 sm:border-r sm:border-neutral-200/80">
                    {tabsConfig.map((tab) => {
                        const IconComponent = tab.icon;
                        return (
                            <NavLink
                                key={tab.to}
                                to={tab.to}
                                className={({ isActive }) =>
                                    `no-decor group w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl font-medium text-xs transition-all select-none border-0 ${
                                        isActive
                                            ? 'bg-black !text-white shadow-xs'
                                            : 'bg-transparent !text-neutral-700 hover:bg-neutral-100 hover:!text-black'
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <IconComponent
                                                size={18}
                                                stroke={1.6}
                                                className={isActive ? '!text-white flex-shrink-0' : '!text-neutral-500 group-hover:!text-black flex-shrink-0 transition-colors'}
                                            />
                                            <span className={`text-left font-medium whitespace-nowrap ${isActive ? '!text-white' : '!text-neutral-800 group-hover:!text-black'}`}>
                                                {t(tab.labelKey)}
                                            </span>
                                        </div>
                                        {tab.count !== undefined && (
                                            <div
                                                className={`text-[11px] px-2 py-0.5 rounded-md font-semibold flex-shrink-0 ml-2 ${
                                                    isActive
                                                        ? 'bg-neutral-800 !text-white border border-neutral-700'
                                                        : 'bg-neutral-100 !text-neutral-600 border border-neutral-200/60'
                                                }`}
                                            >
                                                {tab.count}/{tab.total}
                                            </div>
                                        )}
                                    </>
                                )}
                            </NavLink>
                        );
                    })}
                </div>

                {/* Route Content Area */}
                <div className="flex-1 min-w-0 sm:pl-6 pt-6 sm:pt-0 w-full">
                    <Outlet context={{ user: userData }} />
                </div>
            </div>

            {/* 3. BOTTOM SECTION: Transparent Footer with Metadata & Danger Zone */}
            <div className="px-2 py-1 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <ShowMetadata item={userData} />

                <Button
                    variant="subtle"
                    color="red"
                    size="sm"
                    loading={deleteUserMutation.isPending}
                    onClick={handleDelete}
                    leftSection={<IconTrash size={16} />}
                >
                    {t('delete_user')}
                </Button>
            </div>
        </div>
    );
}
